import { z } from 'zod'

z.setErrorMap((issue, ctx) => {
  console.error(
    // @ts-expect-error
    `Zod Error "${ctx.defaultError}": ${issue.code} at path '${issue.path.join('.')}'. Expected ${issue.expected}, received ${issue.received}. Data: ${JSON.stringify(ctx.data) || ctx.data}`
  )
  return { message: ctx.defaultError }
})

const Link = z.object({
  href: z.string(),
  title: z.string().optional(),
})
const PageResponse = z.object({
  number: z.number().int(),
  size: z.number().int(),
})
const Paginated = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    _links: z.object({
      self: Link,
    }),
    total: z.number().int(),
    page: PageResponse,
    _embedded: z.object({
      items: z.array(itemSchema),
    }),
  })

export const User = z.object({
  login: z.string(),
  name: z.string().nullable(),
  avatar_url: z.string(),
  email: z.string(),
})
export type User = z.infer<typeof User>

export const Account = z.object({
  id: z.number(),
  login: z.string(),
  avatar_url: z.string(),
  type: z.enum(['User', 'Organization']),
})
export type Account = z.infer<typeof Account>

export const RepositoryType = z.enum(['git', 'azure', 'github', 'gitlab', 'bitbucket'])
export type RepositoryType = z.infer<typeof RepositoryType>
const BaseRepository = z.object({
  type: RepositoryType,
  _links: z.object({
    self: Link,
    'latest-analysis': Link,
    workflows: Link,
  }),
  id: z.string(),
  name: z.string(),
  default_branch: z.string(),
})
const AzureRepositoryResponse = BaseRepository.extend({
  type: z.literal('azure'),
  organization: z.string(),
  project: z.string(),
})
const GitRepositoryResponse = BaseRepository.extend({
  type: z.literal('git'),
})
const GitHubRepositoryResponse = BaseRepository.extend({
  type: z.literal('github'),
  html_url: z.string(),
})
const GitLabRepositoryResponse = BaseRepository.extend({
  type: z.literal('gitlab'),
  namespace: z.string(),
})
const BitbucketRepositoryResponse = BaseRepository.extend({
  type: z.literal('bitbucket'),
  workspace: z.string(),
  slug: z.string(),
})
export const Repository = z.discriminatedUnion('type', [
  GitHubRepositoryResponse,
  GitLabRepositoryResponse,
  AzureRepositoryResponse,
  BitbucketRepositoryResponse,
  GitRepositoryResponse,
])
export type Repository = z.infer<typeof Repository>
export const PaginatedRepositories = Paginated(Repository)
export type PaginatedRepositories = z.infer<typeof PaginatedRepositories>

export const Tool = z.enum([
  'APPSCAN',
  'POLARIS',
  'CHECKMARX',
  'CODEQL',
  'CONTRAST',
  'DEFECT_DOJO',
  // NOTE 28/01/2025: Pixee enum seems to be never used in practice, we don't implement it in scan analysis page
  'PIXEE',
  'SEMGREP',
  'SNYK',
  'SONAR',
  'VERACODE',
])
export type Tool = z.infer<typeof Tool>
const ToolLowercase = z.enum(['appscan', 'checkmarx', 'codeql', 'contrast', 'defectdojo', 'semgrep', 'snyk', 'sonar', 'polaris', 'veracode'])
export type ToolLowercase = z.infer<typeof ToolLowercase>
export const CreateScanResponse = z.object({
  _links: z.object({
    self: Link,
    repository: Link.extend({
      title: z.string(),
    }),
    scale: Link,
  }),
  id: z.string(),
  detector: Tool,
  sha: z.string().nullable(),
  scanned_at: z.string().nullable(),
  imported_at: z.string(),
  html_url: z.string().nullable(),
  branch: z.string().nullable(),
})
export type CreateScanResponse = z.infer<typeof CreateScanResponse>
const CreateScanRequestMetadata = z.object({
  tool: ToolLowercase,
  sha: z.string().optional(),
  branch: z.string().optional(),
})
type CreateScanRequestMetadata = z.infer<typeof CreateScanRequestMetadata>
export const CreateScanRequestFormData = z.object({
  metadata: CreateScanRequestMetadata,
  files: z.record(z.string(), z.instanceof(File)).optional(),
})
export type CreateScanRequestFormData = z.infer<typeof CreateScanRequestFormData>
export const Credentials = z.object({
  username: z.string(),
  password: z.string(),
})
export type Credentials = z.infer<typeof Credentials>

const RatingResponse = z.object({
  description: z.string().min(1),
  score: z.number().min(0).max(100),
})
export const Fix = z.object({
  id: z.string(),
  result: z.enum(['completed_results', 'completed_no_results', 'failed']),
  codemod: z.string(),
  summary: z.string().nullable(),
  description: z.string().nullable(),
  strategy: z.enum(['provisional_ai', 'ai', 'deterministic', 'hybrid']),
  quality: z
    .object({
      safety: RatingResponse,
      effectiveness: RatingResponse,
      cleanliness: RatingResponse,
    })
    .nullable(),
  confidence: z.enum(['high', 'medium', 'low']).nullable().describe('The confidence we have in the fix'),
  _links: z.object({
    self: Link,
    changesets: Link,
  }),
})
export type Fix = z.infer<typeof Fix>
export const PaginatedFixes = Paginated(Fix)
export type PaginatedFixes = z.infer<typeof PaginatedFixes>

export const Changeset = z.object({
  path: z.string(),
  preview: z.string(),
})
export type Changeset = z.infer<typeof Changeset>
export const PaginatedChangesets = Paginated(Changeset)
export type PaginatedChangesets = z.infer<typeof PaginatedChangesets>

export const AnalysisStateResponse = z.enum(['queued', 'in_progress', 'skipped', 'completed', 'failed'])
export type AnalysisStateResponse = z.infer<typeof AnalysisStateResponse>
const AnalysisStateLogResponse = z.object({
  state: AnalysisStateResponse,
  timestamp: z.string(),
})
type AnalysisStateLogResponse = z.infer<typeof AnalysisStateLogResponse>
export const ScanAnalysis = z.object({
  id: z.string(),
  current_state: AnalysisStateLogResponse,
  log: z.array(AnalysisStateLogResponse),
  sha: z.string().optional().nullable(),
  branch: z.string().optional().nullable().describe('[@deprecated] Use _embedded.scan.branch instead'),
  _links: z.object({
    self: Link,
    scan: Link,
    findings: Link,
    patches: Link,
  }),
  _embedded: z.object({
    scan: z.object({
      _links: z.object({
        self: Link,
        repository: Link,
        scale: Link,
      }),
      id: z.string(),
      detector: Tool,
      sha: z.string(),
      scanned_at: z.string().nullable(),
      imported_at: z.string(),
      html_url: z.string().nullable(),
      branch: z.string().nullable(),
    }),
  }),
})
export type ScanAnalysis = z.infer<typeof ScanAnalysis>
export const PaginatedAnalysisResponse = z.object({
  _links: z.object({
    self: Link,
  }),
  total: z.number(),
  page: PageResponse,
  _embedded: z.object({
    items: z.array(ScanAnalysis),
  }),
})
export type PaginatedAnalysisResponse = z.infer<typeof PaginatedAnalysisResponse>
export const CreatePatchRequest = z.object({
  findings: z.array(z.string()).min(1).max(1),
})
export type CreatePatchRequest = z.infer<typeof CreatePatchRequest>
export const AzureOrganization = z.object({
  name: z.string(),
})
export type AzureOrganization = z.infer<typeof AzureOrganization>
export const AzureProject = z.object({
  id: z.string(),
  name: z.string(),
})
export type AzureProject = z.infer<typeof AzureProject>
export const AzureRepository = z.object({
  id: z.string(),
  name: z.string(),
  project: AzureProject,
  pixee_id: z.string().nullable(),
})
export type AzureRepository = z.infer<typeof AzureRepository>

export const HALPaginatedAzureOrganizations = Paginated(AzureOrganization)
export type HALPaginatedAzureOrganizations = z.infer<typeof HALPaginatedAzureOrganizations>

export const HALPaginatedAzureProjects = Paginated(AzureProject)
export type HALPaginatedAzureProjects = z.infer<typeof HALPaginatedAzureProjects>

export const HALPaginatedAzureRepositories = Paginated(AzureRepository)
export type HALPaginatedAzureRepositories = z.infer<typeof HALPaginatedAzureRepositories>

export const CreateWorkflowRequest = z.object({
  name: z.string().min(1).optional(),
  tool: ToolLowercase,
  event: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('schedule'),
      cadence: z.enum(['daily', 'weekly']),
      start: z.string().datetime().optional(),
    }),
    z.object({
      type: z.literal('new-scan'),
      branch: z.string().optional(),
    }),
  ]),
  action: z.object({
    type: z.literal('create-patch'),
    findings: z
      .object({
        include_severity_labels: z.array(z.string()).optional(),
        minimum_severity_score: z.number().optional(),
        maximum_severity_score: z.number().optional(),
      })
      .optional(),
    fixes: z
      .object({
        minimum_fix_confidence: z.enum(['high', 'medium', 'low']),
      })
      .optional(),
    limit: z
      .object({
        type: z.literal('finding-count'),
        max: z.number(),
      })
      .optional(),
  }),
})
export type CreateWorkflowRequest = z.infer<typeof CreateWorkflowRequest>

export const FindingTriageArticle = z.string()
export type FindingTriageArticle = z.infer<typeof FindingTriageArticle>

export const FindingTriageReport = z.string()
export type FindingTriageReport = z.infer<typeof FindingTriageReport>

export const CreateWorkflowResponse = z.object({
  type: z.string(),
  _links: z.object({
    self: Link,
    repository: Link,
  }),
  id: z.string(),
  name: z.string().nullable(),
})
export type CreateWorkflowResponse = z.infer<typeof CreateWorkflowResponse>

export const CreateAnalysisResponse = z.object({
  _links: z.object({
    self: Link,
    findings: Link,
    patches: Link,
    scan: Link,
  }),
  id: z.string(),
  current_state: z.object({
    state: z.string(),
    timestamp: z.string(),
  }),
  log: z.array(
    z.object({
      state: z.string(),
      timestamp: z.string(),
    })
  ),
  sha: z.string().nullable(),
  branch: z.string().nullable(),
  _embedded: z.object({
    scan: CreateScanResponse,
  }),
})
export type CreateAnalysisResponse = z.infer<typeof CreateAnalysisResponse>

export const CreateRepositoryResponse = z.object({
  type: z.string(),
  _links: z.object({
    self: Link,
    'latest-analysis': Link,
  }),
  id: z.string(),
  name: z.string(),
  default_branch: z.string().nullable(),
})
export type CreateRepositoryResponse = z.infer<typeof CreateRepositoryResponse>

export const GitlabProject = z.object({
  path: z.string(),
  namespace_full_path: z.string(),
  name_with_namespace: z.string(),
  pixee_id: z.string().optional().nullable(),
})
export type GitlabProject = z.infer<typeof GitlabProject>
export const PaginatedGitlabProjects = Paginated(GitlabProject)
export type PaginatedGitlabProjects = z.infer<typeof PaginatedGitlabProjects>

export const Workflow = z.object({
  _links: z.object({
    self: Link,
    repository: Link,
  }),
  id: z.string(),
  name: z.string().nullable(),
})
export type Workflow = z.infer<typeof Workflow>
export const PaginatedWorkflows = Paginated(Workflow)
export type PaginatedWorkflows = z.infer<typeof PaginatedWorkflows>

const BaseIntegration = z.object({
  id: z.string(),
  type: z.string(),
  _links: z.object({
    self: Link,
  }),
})
const SonarIntegration = BaseIntegration.extend({
  type: z.literal('sonar'),
  base_uri: z.string().url(),
  token_configured: z.boolean(),
  webhook_secret_configured: z.boolean(),
})
export type SonarIntegration = z.infer<typeof SonarIntegration>
const GitHubIntegration = BaseIntegration.extend({
  type: z.literal('github'),
  app_name: z.string(),
})
export type GitHubIntegration = z.infer<typeof GitHubIntegration>
const GitLabIntegration = BaseIntegration.extend({
  type: z.literal('gitlab'),
})
export type GitLabIntegration = z.infer<typeof GitLabIntegration>
const AzureIntegration = BaseIntegration.extend({
  type: z.literal('azure'),
})
export type AzureIntegration = z.infer<typeof AzureIntegration>
const BitbucketIntegration = BaseIntegration.extend({
  type: z.literal('bitbucket'),
})
export type BitbucketIntegration = z.infer<typeof BitbucketIntegration>
const VeracodeIntegration = BaseIntegration.extend({
  type: z.literal('veracode'),
  base_uri: z.string().url(),
})
export type VeracodeIntegration = z.infer<typeof VeracodeIntegration>
const AppScanIntegration = BaseIntegration.extend({
  type: z.literal('appscan'),
})
export type AppScanIntegration = z.infer<typeof AppScanIntegration>
export const Integration = z.discriminatedUnion('type', [
  SonarIntegration,
  GitHubIntegration,
  GitLabIntegration,
  AzureIntegration,
  BitbucketIntegration,
  VeracodeIntegration,
  AppScanIntegration,
])
export type Integration = z.infer<typeof Integration>
export const PaginatedIntegrations = Paginated(Integration)
export type PaginatedIntegrations = z.infer<typeof PaginatedIntegrations>

export const Scale = z.object({
  type: z.enum(['ranked-set', 'score']),
  _links: z.object({
    self: Link,
  }),
  id: z.string(),
  severities: z
    .array(
      z.object({
        label: z.string(),
        rank: z.number(),
      })
    )
    .optional(),
  min: z.number().optional(),
  max: z.number().optional(),
})
export type Scale = z.infer<typeof Scale>
export const PaginatedScales = Paginated(Scale)
export type PaginatedScales = z.infer<typeof PaginatedScales>

export const PatchState = z.enum(['in_progress', 'completed_success', 'completed_failed'])
export type PatchState = z.infer<typeof PatchState>
const BasePatch = z.object({
  id: z.string(),
  state: PatchState,
  _links: z.object({
    self: Link,
  }),
})
const GitPatchResponse = BasePatch.extend({
  type: z.literal('git'),
  branch_name: z.string().nullable(),
})
type GitPatchResponse = z.infer<typeof GitPatchResponse>
const PullRequestPatchBase = BasePatch.extend({
  html_url: z.string().url().nullable(),
  pr_number: z.number().int().positive().nullable(),
})
const GitHubPatchResponse = PullRequestPatchBase.extend({
  type: z.literal('github'),
})
type GitHubPatchResponse = z.infer<typeof GitHubPatchResponse>
const GitLabPatchResponse = PullRequestPatchBase.extend({
  type: z.literal('gitlab'),
})
type GitLabPatchResponse = z.infer<typeof GitLabPatchResponse>
const AzurePatchResponse = PullRequestPatchBase.extend({
  type: z.literal('azure'),
})
type AzurePatchResponse = z.infer<typeof AzurePatchResponse>
const BitbucketPatchResponse = PullRequestPatchBase.extend({
  type: z.literal('bitbucket'),
})
type BitbucketPatchResponse = z.infer<typeof BitbucketPatchResponse>
export const Patch = z.discriminatedUnion('type', [
  GitPatchResponse,
  GitHubPatchResponse,
  GitLabPatchResponse,
  AzurePatchResponse,
  BitbucketPatchResponse,
])
export type Patch = z.infer<typeof Patch>

export const Severity = z.object({
  label: z.string().nullable(),
  rank: z.number().min(0).max(100),
})
export type Severity = z.infer<typeof Severity>
export const Finding = z.object({
  self: Link,
  id: z.string(),
  title: z.string().nullable(),
  rule: z.string(),
  severity: Severity.nullable().optional(),
  _links: z.object({
    self: Link,
    analysis: Link,
    fixes: Link,
    scan: Link,
    triage: Link,
    'latest-patch': Link.optional(),
    article: Link,
    report: Link,
  }),
  _embedded: z.object({
    triage: z
      .object({
        result: z.enum(['triaged', 'error', 'rule_not_supported']),
        suggested_status: z.enum(['wont_fix', 'false_positive', 'true_positive', 'suspicious']).nullable(),
        suggested_severity: Severity.nullable().optional(),
        summary: z.string().nullable(),
      })
      .nullable(),
    fixes: z.object({
      total: z.number().int(),
      _links: z.object({
        self: Link,
      }),
    }),
    'latest-patch': Patch.nullable(),
  }),
})
export type Finding = z.infer<typeof Finding>
export const PaginatedFindings = Paginated(Finding).extend({
  false_positives: z.number(),
  true_positives: z.number(),
  suspicious: z.number(),
  wont_fix: z.number(),
  fixes: z.number(),
})
export type PaginatedFindings = z.infer<typeof PaginatedFindings>
